import './Mortgage2StarterLink.css';

import React from 'react';
import { Typography } from '@vtblife/uikit';

import classname from '@search/classname/src';

import { ClassifiedAnalytics } from '@search/vtbeco-frontend-core/domain/offer/analytics/ClassifiedAnalytics';

import { formatLocaleNumber } from '@search/helpers/src/formatNumber';
import { useClassifiedOfferDataLayerPush } from '../../../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import {
    useMinimalBanksMortgageOverall,
    getMinimalBanksMortgageEconomyPercent
} from '../../useMinimalBanksMortageState';
import { MinimalBanksMortgageEconomy } from '../../MinimalBanksMortgageEconomy';
import type { Mortgage2ContactsFormSendProps } from '../mortgage2ContactsFormSend';
import { mortgage2ParamsFromClasified } from '../mortgage2ParamsFromClasified';
import type { Mortgage2StarterProps } from '../useMortgage2User';
import type { Mortgage2RealtyType } from '../mortgageTypes';
import { defaultMortgageRateSecondFormatted } from '../../contants';

const cn = classname.bind(null, 'Mortgage2StarterLink');

export function Mortgage2StarterLink(
    {
        className,
        regionId,
        mortgageBannerMinutesVariant,
        mortgageBannerTextVariant,
        realtyType,
        isSnippetLink,
        isSecondSnippetLink,
        gtmSendId,
        gtmShowId,
        linkGtmId = ClassifiedAnalytics.IpotekaFromOffer,
        locationPlacement,
        offerUrl,
        onClick,
        scrollToMortgageCalculator,
        variantForOtherRegions,
        minimalMortgageRate,
        ...stateParams
    }: {
        linkGtmId?: string;
        isSnippetLink?: boolean;
        isSecondSnippetLink?: boolean;
        variantForOtherRegions?: boolean;
        className?: string;
        regionId: number;
        realtyType: Mortgage2RealtyType;
        onClick?: () => void;
        scrollToMortgageCalculator?: () => void;
        propertyCost: number;
        minimalMortgageRate: number | null;
    } & Omit<Mortgage2StarterProps, 'mortgageRegionId'>
    & Omit<Mortgage2ContactsFormSendProps, 'message' | 'applicationId'>
) {
    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const onLinkClick = React.useCallback((e?: React.MouseEvent<HTMLElement>) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (linkGtmId) dataLayerPush({ event: linkGtmId });
        scrollToMortgageCalculator?.();
    }, [ linkGtmId, dataLayerPush, onClick, scrollToMortgageCalculator ]);

    const { economyFormatted, maxEconomyFormatted } = getMinimalBanksMortgageEconomyPercent();

    const response = useMinimalBanksMortgageOverall(
        mortgage2ParamsFromClasified({ ...stateParams, regionId })
    );

    const linkTitle = React.useMemo(() => {
        if (isSecondSnippetLink) return `Ипотека на\u00A0вторичку от\u00A0${formatLocaleNumber(minimalMortgageRate) || defaultMortgageRateSecondFormatted}%`;

        return 'Рассчитать ипотеку';
    }, [ isSecondSnippetLink ]);

    return (<div className={cn(null, undefined, className)}>
        {economyFormatted && (
            <Typography variant='primary-alone' color='secondary'>
                В ипотеку
            </Typography>
        )}
        {response?.allMinimalPaymentFormatted && (
            <Typography variant='primary-alone' bold className={cn('marginTop')}>
                от {response.allMinimalPaymentFormatted}
            </Typography>
        )}
        {maxEconomyFormatted && (
            <Typography variant='secondary-alone' className={cn('flex')}>
                Сэкономьте до&nbsp;<MinimalBanksMortgageEconomy
                    value={maxEconomyFormatted}
                    underline={! variantForOtherRegions}
                    dotted={variantForOtherRegions}
                /> на&nbsp;ставке вместе с&nbsp;«Метром&nbsp;квадратным»
            </Typography>
        )}
        <Typography
            onClick={onLinkClick}
            data-test='snippet-mortgage-link'
            className={cn('link')}
            color='blue-500'
            bold
        >
            {linkTitle}
        </Typography>
    </div>);
}
